/**
 * Time To Surgery Module - PSP/CRE Tool v3.0
 * 
 * @module timeToSurgeryModule
 * @version 1.1.0
 * @date February 23, 2026
 * @author Dr. Tatar Radu MD
 *
 * VERSION 1.1.0 - Phase 4-D constants migration analysis (February 23, 2026)
 *
 * All 5 TTS items DEFERRED — canonical destination files not available:
 * - TTS-01 DEFERRED: DEFAULT_TIME_SEGMENTS → evacuationParadigms.js
 *   (evacuationParadigms.js not in working directory; structural alignment unverified)
 * - TTS-02 DEFERRED: TREATMENT_TIMES → mascalThresholds.js
 *   (mascalThresholds.js has no treatment timing block; would require EXT step first)
 * - TTS-03 DEFERRED: GOLDEN_HOUR_THRESHOLDS → evacuationParadigms.js
 *   (evacuationParadigms.js not in working directory; reconciliation with T1_SURVIVAL_BY_TIME needed)
 * - TTS-04 DEFERRED: SURGICAL_TEAMS → surgicalCapacity.js SURGICAL_TEAM_COMPOSITION
 *   (structural mismatch: FST personnel counts differ — local surgeons=2, canonical=3;
 *    FRS type not present in SURGICAL_TEAM_COMPOSITION; value-level discrepancies must
 *    be adjudicated by Dr. Tatar before promotion)
 * - TTS-05 DEFERRED: Replace local defs with imports — depends on TTS-01..04
 * 
 * @description
 * Calculates cumulative time from Point of Injury (POI) to first surgical capability.
 * Golden Hour is POI→Surgery, NOT per evacuation leg.
 * 
 * Issue ID: CC-004 Phase 6
 * Priority: HIGH
 * 
 * Doctrinal Reference:
 * - NATO AJMedP-2: Allied Joint Medical Evacuation Doctrine
 * - NATO AJMedP-7: Allied Joint Medical Planning
 * - US Army ATP 4-02.2: Medical Evacuation
 * - US Army ATP 4-02.25: Forward Surgical Team Operations
 * - Ukraine 2022-2024: Observed 90-180 min average POI-to-surgery times
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default time segments in the evacuation chain (minutes)
 * TTS-01 DEFERRED: Promote to evacuationParadigms.js and import from there.
 * evacuationParadigms.js not in working directory. Structural alignment with
 * any existing evacuation timing data in that file must be verified first.
 */
export const DEFAULT_TIME_SEGMENTS = {
  POI_TO_CCP: 15,           // Self/buddy aid, CLS carry
  CCP_TO_ROLE1: 20,         // Ambulance collection (PULL)
  ROLE1_TREATMENT: 20,      // Stabilization time
  ROLE2B_TREATMENT: 30,     // Advanced stabilization (if no surgery)
  ROLE2E_TREATMENT: 0,      // Surgery destination - no additional treatment time
  ROLE3_TREATMENT: 0        // Surgery destination - no additional treatment time
};

/**
 * Treatment times by facility type (minutes)
 * TTS-02 DEFERRED: Promote to mascalThresholds.js.
 * mascalThresholds.js has no existing treatment timing block — would require
 * an EXT step to add the block before this module can import from it.
 * Values (CCP=5, R1=20, R2B=30) are currently authoritative here.
 */
export const TREATMENT_TIMES = {
  CCP: 5,                   // Minimal - collection point only
  ROLE1: 20,                // Stabilization, triage, initial care
  ROLE2_BASIC: 30,          // Advanced stabilization, resuscitation
  ROLE2_BASIC_FST: 0,       // Surgery destination when FST attached
  ROLE2_ENHANCED: 0,        // Surgery destination
  ROLE3: 0,                 // Surgery destination
  ROLE4: 0                  // Surgery destination
};

/**
 * Facility types with surgical capability
 */
export const SURGICAL_FACILITY_TYPES = [
  'ROLE2_ENHANCED',
  'ROLE2E',
  'ROLE3',
  'ROLE4'
];

/**
 * Golden Hour thresholds and DOW modifiers
 * Based on Ukraine 2022-2024 data
 * TTS-03 DEFERRED: Promote to evacuationParadigms.js and reconcile with any
 * T1_SURVIVAL_BY_TIME data already there. evacuationParadigms.js not in working
 * directory. survivalModule.js and casualtyCalculator.js should import from
 * evacuationParadigms.js once promotion is done. Currently authoritative here.
 */
export const GOLDEN_HOUR_THRESHOLDS = {
  GOLDEN_HOUR: {
    max_minutes: 60,
    dow_modifier: 1.0,
    status: 'COMPLIANT',
    color: 'success',
    description: 'Golden Hour achieved - optimal survival'
  },
  PLATINUM_TEN: {
    max_minutes: 10,
    dow_modifier: 0.85,
    status: 'OPTIMAL',
    color: 'success',
    description: 'Platinum 10 minutes - critical hemorrhage control'
  },
  EXTENDED_60_90: {
    max_minutes: 90,
    dow_modifier: 1.15,
    status: 'MARGINAL',
    color: 'warning',
    description: '15% increased DOW risk'
  },
  EXTENDED_90_120: {
    max_minutes: 120,
    dow_modifier: 1.35,
    status: 'DEGRADED',
    color: 'warning',
    description: '35% increased DOW risk'
  },
  EXTENDED_120_180: {
    max_minutes: 180,
    dow_modifier: 1.60,
    status: 'CRITICAL',
    color: 'error',
    description: '60% increased DOW risk'
  },
  BEYOND_180: {
    max_minutes: Infinity,
    dow_modifier: 2.0,
    status: 'SEVERE',
    color: 'error',
    description: 'Doubled DOW rate - critical care gap'
  }
};

/**
 * Forward Surgical Team specifications
 * TTS-04 DEFERRED: Promote to surgicalCapacity.js SURGICAL_TEAM_COMPOSITION.
 * REASON: Value-level discrepancies require adjudication before promotion:
 *   - FST.personnel.surgeons: local=2, canonical FORWARD_SURGICAL_TEAM=3
 *   - FST.personnel.total: local=10, canonical=16
 *   - FST.capability.surgical_capacity_per_day: local=8, canonical procedures_per_day=10
 *   - FRS type: present locally, not in SURGICAL_TEAM_COMPOSITION at all
 * Do not auto-promote — reconcile values with Dr. Tatar first.
 * Currently authoritative here for timeToSurgeryModule logic.
 */
export const SURGICAL_TEAMS = {
  FST: {
    type: 'FST',
    name: 'Forward Surgical Team',
    nato_designation: 'FST',
    personnel: {
      surgeons: 2,
      anesthesiologists: 1,
      nurses: 3,
      surgical_techs: 4,
      total: 10
    },
    capability: {
      type: 'DAMAGE_CONTROL',
      surgical_capacity_per_day: 8,
      minor_procedures_per_day: 20,
      blood_units_per_day: 40
    },
    can_attach_to: ['ROLE2_BASIC', 'ROLE1'],
    logistics: {
      setup_time_hours: 2,
      teardown_time_hours: 1,
      mobility: 'HIGH',
      transport_required: '2x HMMWV or 1x M997',
      weight_kg: 2500,
      power_required_kw: 15
    },
    when_attached_provides: {
      has_surgical: true,
      surgical_teams: 1,
      changes_golden_hour_destination: true
    }
  },
  FRS: {
    type: 'FRS',
    name: 'Forward Resuscitative Surgery',
    nato_designation: 'FRS',
    personnel: {
      surgeons: 1,
      anesthesiologists: 1,
      nurses: 2,
      surgical_techs: 2,
      total: 6
    },
    capability: {
      type: 'RESUSCITATIVE',
      surgical_capacity_per_day: 4,
      minor_procedures_per_day: 10,
      blood_units_per_day: 20
    },
    can_attach_to: ['ROLE2_BASIC'],
    logistics: {
      setup_time_hours: 1,
      teardown_time_hours: 0.5,
      mobility: 'VERY_HIGH',
      transport_required: '1x HMMWV',
      weight_kg: 800,
      power_required_kw: 8
    },
    when_attached_provides: {
      has_surgical: true,
      surgical_teams: 1,
      changes_golden_hour_destination: true
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if facility has surgical capability (including attached FST/FRS)
 * @param {Object} facility - Facility object
 * @returns {boolean} True if facility can perform surgery
 */
export const hasSurgicalCapability = (facility) => {
  if (!facility) return false;
  
  // Built-in surgical capability
  const facilityType = facility.type || facility.role;
  if (SURGICAL_FACILITY_TYPES.includes(facilityType)) {
    return true;
  }
  
  // Check for attached surgical teams
  if (facility.attached_surgical_teams?.length > 0) {
    return facility.attached_surgical_teams.some(team => 
      team.status === 'OPERATIONAL' || team.status === undefined
    );
  }
  
  // Check has_surgical flag (may be set by other logic)
  if (facility.has_surgical === true) {
    return true;
  }
  
  return false;
};

/**
 * Get treatment time for facility type
 * @param {string} facilityType - Facility type string
 * @param {boolean} hasFST - Whether FST is attached
 * @returns {number} Treatment time in minutes
 */
export const getTreatmentTime = (facilityType, hasFST = false) => {
  // If FST attached to Role 2B, it becomes surgery destination
  if (hasFST && (facilityType === 'ROLE2_BASIC' || facilityType === 'ROLE2B')) {
    return TREATMENT_TIMES.ROLE2_BASIC_FST;
  }
  
  // Normalize facility type
  const normalizedType = facilityType?.replace('ROLE2B', 'ROLE2_BASIC')
                                      ?.replace('ROLE2E', 'ROLE2_ENHANCED');
  
  return TREATMENT_TIMES[normalizedType] || 15;
};

/**
 * Get DOW modifier based on time to surgery
 * @param {number} minutes - Cumulative time to surgery
 * @returns {Object} DOW modifier info
 */
export const getDOWModifier = (minutes) => {
  if (minutes <= GOLDEN_HOUR_THRESHOLDS.PLATINUM_TEN.max_minutes) {
    return GOLDEN_HOUR_THRESHOLDS.PLATINUM_TEN;
  }
  if (minutes <= GOLDEN_HOUR_THRESHOLDS.GOLDEN_HOUR.max_minutes) {
    return GOLDEN_HOUR_THRESHOLDS.GOLDEN_HOUR;
  }
  if (minutes <= GOLDEN_HOUR_THRESHOLDS.EXTENDED_60_90.max_minutes) {
    return GOLDEN_HOUR_THRESHOLDS.EXTENDED_60_90;
  }
  if (minutes <= GOLDEN_HOUR_THRESHOLDS.EXTENDED_90_120.max_minutes) {
    return GOLDEN_HOUR_THRESHOLDS.EXTENDED_90_120;
  }
  if (minutes <= GOLDEN_HOUR_THRESHOLDS.EXTENDED_120_180.max_minutes) {
    return GOLDEN_HOUR_THRESHOLDS.EXTENDED_120_180;
  }
  return GOLDEN_HOUR_THRESHOLDS.BEYOND_180;
};

/**
 * Find facility by ID in facilities array
 * @param {Array} facilities - Array of all facilities
 * @param {string} facilityId - ID to find
 * @returns {Object|null} Found facility or null
 */
const findFacilityById = (facilities, facilityId) => {
  if (!facilities || !facilityId) return null;
  return facilities.find(f => f.id === facilityId || f.facility_id === facilityId);
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate cumulative time from POI to surgical capability
 * 
 * @param {Object} startFacility - Starting facility (typically Role 1)
 * @param {Array} allFacilities - All facilities for chain traversal
 * @param {Object} config - Configuration options:
 *   - poi_to_ccp_min: Time from POI to CCP (default 15)
 *   - ccp_to_role1_min: Time from CCP to Role 1 (default 20)
 *   - treatment_time_overrides: Override treatment times by facility type
 * 
 * @returns {Object} Cumulative time analysis:
 *   - cumulative_time_min: Total time to surgery
 *   - golden_hour_compliant: Boolean
 *   - surgical_facility: Facility where surgery occurs
 *   - segments: Array of time segments
 *   - dow_modifier: DOW rate modifier
 */
export const calculateTimeToSurgery = (startFacility, allFacilities, config = {}) => {
  // Validate inputs
  if (!startFacility) {
    console.error('[TimeToSurgery] No starting facility provided');
    return createEmptyResult();
  }
  
  if (!allFacilities || !Array.isArray(allFacilities)) {
    allFacilities = [];
  }
  
  // Extract config with defaults
  const poiToCcpMin = config.poi_to_ccp_min ?? DEFAULT_TIME_SEGMENTS.POI_TO_CCP;
  const ccpToRole1Min = config.ccp_to_role1_min ?? DEFAULT_TIME_SEGMENTS.CCP_TO_ROLE1;
  const treatmentOverrides = config.treatment_time_overrides || {};
  
  // Initialize tracking
  const segments = [];
  let currentFacility = startFacility;
  let cumulativeTime = 0;
  let facilitiesVisited = [];
  
  // Add POI → CCP → Role 1 time (pre-facility chain)
  segments.push({
    from: 'POI',
    to: 'CCP',
    time_min: poiToCcpMin,
    method: 'SELF_BUDDY',
    description: 'Self/buddy aid, CLS carry'
  });
  cumulativeTime += poiToCcpMin;
  
  segments.push({
    from: 'CCP',
    to: startFacility.id || startFacility.name || 'ROLE1',
    time_min: ccpToRole1Min,
    method: 'PULL',
    description: 'Ambulance collection'
  });
  cumulativeTime += ccpToRole1Min;
  
  // Traverse evacuation chain until surgical capability found
  let maxIterations = 10; // Prevent infinite loops
  let iterations = 0;
  
  while (currentFacility && iterations < maxIterations) {
    iterations++;
    facilitiesVisited.push(currentFacility.id || currentFacility.name);
    
    const facilityType = currentFacility.type || currentFacility.role;
    const hasFST = currentFacility.attached_surgical_teams?.length > 0;
    
    // Get treatment time (may be overridden)
    const treatmentTime = treatmentOverrides[facilityType] ?? 
                          getTreatmentTime(facilityType, hasFST);
    
    // Add treatment time at this facility
    if (treatmentTime > 0) {
      segments.push({
        from: currentFacility.id || currentFacility.name,
        to: currentFacility.id || currentFacility.name,
        time_min: treatmentTime,
        method: 'TREATMENT',
        description: `${facilityType} stabilization/treatment`
      });
      cumulativeTime += treatmentTime;
    }
    
    // Check if this facility has surgery
    if (hasSurgicalCapability(currentFacility)) {
      const dowInfo = getDOWModifier(cumulativeTime);
      
      return {
        cumulative_time_min: cumulativeTime,
        golden_hour_compliant: cumulativeTime <= 60,
        surgical_facility: currentFacility,
        surgical_facility_id: currentFacility.id,
        surgical_facility_type: facilityType,
        has_fst_attached: hasFST,
        segments,
        facilities_visited: facilitiesVisited,
        dow_modifier: dowInfo.dow_modifier,
        dow_status: dowInfo.status,
        dow_color: dowInfo.color,
        dow_description: dowInfo.description
      };
    }
    
    // Find next facility in evacuation chain
    const receivingId = currentFacility.evacuation?.receiving_facility_id ||
                        currentFacility.receiving_facility_id ||
                        currentFacility.evacuates_to;
    
    if (receivingId) {
      const nextFacility = findFacilityById(allFacilities, receivingId);
      
      if (nextFacility) {
        // Get evacuation time
        const evacTime = currentFacility.evacuation?.estimated_time_min ||
                         currentFacility.evacuation_time_min ||
                         currentFacility.distance_km ? currentFacility.distance_km * 2 : 30; // Default estimate
        
        const method = currentFacility.evacuation_config?.method ||
                       currentFacility.evacuation?.method ||
                       'PULL';
        
        segments.push({
          from: currentFacility.id || currentFacility.name,
          to: nextFacility.id || nextFacility.name,
          time_min: evacTime,
          method,
          distance_km: currentFacility.evacuation?.distance_km || currentFacility.distance_km,
          description: `${facilityType} → ${nextFacility.type || nextFacility.role} evacuation`
        });
        cumulativeTime += evacTime;
        
        currentFacility = nextFacility;
      } else {
        // Can't find next facility
        break;
      }
    } else {
      // No receiving facility defined
      break;
    }
  }
  
  // No surgical facility found in chain
  const dowInfo = getDOWModifier(cumulativeTime);
  
  return {
    cumulative_time_min: cumulativeTime,
    golden_hour_compliant: false,
    surgical_facility: null,
    surgical_facility_id: null,
    surgical_facility_type: null,
    has_fst_attached: false,
    segments,
    facilities_visited: facilitiesVisited,
    dow_modifier: dowInfo.dow_modifier,
    dow_status: dowInfo.status,
    dow_color: dowInfo.color,
    dow_description: dowInfo.description,
    warning: 'No surgical facility found in evacuation chain'
  };
};

/**
 * Create empty result structure
 */
const createEmptyResult = () => ({
  cumulative_time_min: 0,
  golden_hour_compliant: false,
  surgical_facility: null,
  surgical_facility_id: null,
  surgical_facility_type: null,
  has_fst_attached: false,
  segments: [],
  facilities_visited: [],
  dow_modifier: 1.0,
  dow_status: 'UNKNOWN',
  dow_color: 'default',
  dow_description: 'Unable to calculate',
  warning: 'Invalid input'
});

// ============================================================================
// BATCH CALCULATION FOR ALL FACILITIES
// ============================================================================

/**
 * Calculate time to surgery for all Role 1 facilities
 * 
 * @param {Array} allFacilities - All facilities in the system
 * @param {Object} config - Configuration options
 * @returns {Array} Array of time-to-surgery results per facility
 */
export const calculateAllTimesToSurgery = (allFacilities, config = {}) => {
  if (!allFacilities || !Array.isArray(allFacilities)) {
    return [];
  }
  
  // Find all Role 1 facilities (starting points)
  const role1Facilities = allFacilities.filter(f => {
    const type = f.type || f.role;
    return type === 'ROLE1' || type === 'ROLE_1' || type === 'BAS';
  });
  
  return role1Facilities.map(facility => ({
    facility_id: facility.id,
    facility_name: facility.name,
    parent_unit: facility.parent_unit || facility.battalion_name,
    ...calculateTimeToSurgery(facility, allFacilities, config)
  }));
};

/**
 * Get summary statistics for golden hour compliance
 * 
 * @param {Array} allResults - Results from calculateAllTimesToSurgery
 * @returns {Object} Summary statistics
 */
export const getGoldenHourSummary = (allResults) => {
  if (!allResults || allResults.length === 0) {
    return {
      total_facilities: 0,
      compliant_count: 0,
      non_compliant_count: 0,
      compliance_rate: 0,
      average_time: 0,
      max_time: 0,
      min_time: 0,
      facilities_without_surgery: 0
    };
  }
  
  const compliant = allResults.filter(r => r.golden_hour_compliant);
  const nonCompliant = allResults.filter(r => !r.golden_hour_compliant);
  const withoutSurgery = allResults.filter(r => !r.surgical_facility);
  const times = allResults.map(r => r.cumulative_time_min).filter(t => t > 0);
  
  return {
    total_facilities: allResults.length,
    compliant_count: compliant.length,
    non_compliant_count: nonCompliant.length,
    compliance_rate: Math.round((compliant.length / allResults.length) * 100),
    average_time: times.length > 0 ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0,
    max_time: times.length > 0 ? Math.max(...times) : 0,
    min_time: times.length > 0 ? Math.min(...times) : 0,
    facilities_without_surgery: withoutSurgery.length,
    worst_facilities: nonCompliant
      .sort((a, b) => b.cumulative_time_min - a.cumulative_time_min)
      .slice(0, 5)
      .map(f => ({
        id: f.facility_id,
        name: f.facility_name,
        time_min: f.cumulative_time_min,
        dow_modifier: f.dow_modifier
      }))
  };
};

// ============================================================================
// FST RECOMMENDATION ENGINE
// ============================================================================

/**
 * Generate FST attachment recommendations to improve golden hour compliance
 * 
 * @param {Array} allResults - Results from calculateAllTimesToSurgery
 * @param {Array} allFacilities - All facilities
 * @returns {Array} Recommended FST placements
 */
export const recommendFSTAttachments = (allResults, allFacilities) => {
  if (!allResults || !allFacilities) return [];
  
  const recommendations = [];
  
  // Find non-compliant facilities
  const nonCompliant = allResults.filter(r => 
    !r.golden_hour_compliant && r.cumulative_time_min > 60
  );
  
  // Group by their Role 2 Basic receiving facility
  const byRole2B = {};
  
  nonCompliant.forEach(result => {
    // Find the Role 2B in their chain
    const r2bInChain = result.facilities_visited?.find(id => {
      const fac = allFacilities.find(f => f.id === id);
      return fac?.type === 'ROLE2_BASIC' || fac?.type === 'ROLE2B';
    });
    
    if (r2bInChain) {
      if (!byRole2B[r2bInChain]) {
        byRole2B[r2bInChain] = {
          facility_id: r2bInChain,
          affected_role1s: [],
          total_time_saved: 0
        };
      }
      
      // Calculate time saved if FST attached
      const r2bFacility = allFacilities.find(f => f.id === r2bInChain);
      const timeToR2B = calculateTimeToFacility(result, r2bInChain);
      const timeSaved = result.cumulative_time_min - timeToR2B;
      
      byRole2B[r2bInChain].affected_role1s.push(result.facility_id);
      byRole2B[r2bInChain].total_time_saved += timeSaved;
      byRole2B[r2bInChain].facility_name = r2bFacility?.name;
    }
  });
  
  // Convert to recommendations sorted by impact
  Object.values(byRole2B).forEach(rec => {
    recommendations.push({
      ...rec,
      priority: rec.affected_role1s.length >= 3 ? 'HIGH' : 
                rec.affected_role1s.length >= 2 ? 'MEDIUM' : 'LOW',
      recommendation: `Attach FST to ${rec.facility_name || rec.facility_id} to bring ${rec.affected_role1s.length} battalion(s) into Golden Hour compliance`,
      estimated_compliance_improvement: rec.affected_role1s.length
    });
  });
  
  return recommendations.sort((a, b) => 
    b.affected_role1s.length - a.affected_role1s.length
  );
};

/**
 * Calculate time to reach a specific facility in the chain
 * @private
 */
const calculateTimeToFacility = (result, targetFacilityId) => {
  let time = 0;
  for (const segment of result.segments || []) {
    time += segment.time_min || 0;
    if (segment.to === targetFacilityId) {
      return time;
    }
  }
  return result.cumulative_time_min;
};

// ============================================================================
// MODULE EXPORTS
// ============================================================================

const TimeToSurgeryModule = {
  calculateTimeToSurgery,
  calculateAllTimesToSurgery,
  getGoldenHourSummary,
  recommendFSTAttachments,
  hasSurgicalCapability,
  getTreatmentTime,
  getDOWModifier,
  DEFAULT_TIME_SEGMENTS,
  TREATMENT_TIMES,
  SURGICAL_FACILITY_TYPES,
  GOLDEN_HOUR_THRESHOLDS,
  SURGICAL_TEAMS
};

export default TimeToSurgeryModule;
